import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Download, Upload, Settings, Calendar, CheckCircle, Clock, RefreshCw, Plus, Trash2, Edit, Play } from 'lucide-react';
import Button from '../../components/ui/Button';
import ERPSystemCard from './components/ERPSystemCard';
import IntegrationConfigModal from './components/IntegrationConfigModal';
import ExportSchedulePanel from './components/ExportSchedulePanel';
import ExportHistoryTable from './components/ExportHistoryTable';
import ManualExportWizard from './components/ManualExportWizard';
import FieldMappingEditor from './components/FieldMappingEditor';
import {
  getERPSystems,
  fetchERPIntegrations,
  fetchExportSchedules,
  fetchExportHistory,
  deleteERPIntegration,
  testERPConnection
} from '../../services/erpIntegrationService';

const DataExportIntegrationManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('integrations');
  const [erpSystems] = useState(getERPSystems());
  const [integrations, setIntegrations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [exportHistory, setExportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showExportWizard, setShowExportWizard] = useState(false);
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);

  const companyId = 'demo-company-id'; // Replace with actual company ID from auth context

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [integrationsData, schedulesData, historyData] = await Promise.all([
        fetchERPIntegrations(companyId),
        fetchExportSchedules(companyId),
        fetchExportHistory(companyId, 20)
      ]);

      setIntegrations(integrationsData);
      setSchedules(schedulesData);
      setExportHistory(historyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (integrationId) => {
    try {
      setTestingConnection(integrationId);
      const result = await testERPConnection(integrationId);
      
      if (result?.success) {
        alert(`Connection successful! Latency: ${result?.latency}ms`);
      } else {
        alert(`Connection failed: ${result?.message}`);
      }
    } catch (error) {
      alert(`Connection test failed: ${error?.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!confirm('Are you sure you want to delete this integration?')) return;

    try {
      await deleteERPIntegration(integrationId);
      await loadData();
    } catch (error) {
      alert(`Failed to delete integration: ${error?.message}`);
    }
  };

  const handleEditIntegration = (integration) => {
    setSelectedIntegration(integration);
    setShowConfigModal(true);
  };

  const handleConfigModalClose = async (saved) => {
    setShowConfigModal(false);
    setSelectedIntegration(null);
    if (saved) {
      await loadData();
    }
  };

  const handleExportWizardClose = async (completed) => {
    setShowExportWizard(false);
    if (completed) {
      await loadData();
    }
  };

  const getIntegrationStats = () => {
    const active = integrations?.filter(i => i?.status === 'active')?.length;
    const totalExports = exportHistory?.length;
    const successRate = exportHistory?.length > 0
      ? (exportHistory?.filter(e => e?.status === 'completed')?.length / exportHistory?.length * 100)?.toFixed(1)
      : 0;

    return { active, totalExports, successRate };
  };

  const stats = getIntegrationStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Database className="w-8 h-8 text-blue-600" />
              Data Export & Integration Manager
            </h1>
            <p className="text-gray-600 mt-2">
              Seamless integration with PNG's top ERP systems for automated data synchronization
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowExportWizard(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Manual Export
            </Button>
            <Button
              onClick={() => {
                setSelectedIntegration(null);
                setShowConfigModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Integration
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Integrations</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.active}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Exports</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalExports}</p>
              </div>
              <Upload className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.successRate}%</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Scheduled Exports</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{schedules?.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-500" />
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('integrations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'integrations' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              ERP Integrations
            </button>
            <button
              onClick={() => setActiveTab('schedules')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'schedules' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Export Schedules
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Export History
            </button>
            <button
              onClick={() => setActiveTab('mapping')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mapping' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Field Mapping
            </button>
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      {activeTab === 'integrations' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Supported ERP Systems</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {erpSystems?.map((system) => {
                const integration = integrations?.find(i => i?.erp_system_id === system?.id);
                return (
                  <ERPSystemCard
                    key={system?.id}
                    system={system}
                    integration={integration}
                    onConfigure={() => {
                      setSelectedIntegration(integration);
                      setShowConfigModal(true);
                    }}
                    onTest={() => integration && handleTestConnection(integration?.id)}
                    onDelete={() => integration && handleDeleteIntegration(integration?.id)}
                    testing={testingConnection === integration?.id}
                  />
                );
              })}
            </div>
          </div>

          {integrations?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Integrations</h3>
              <div className="space-y-3">
                {integrations?.map((integration) => {
                  const system = erpSystems?.find(s => s?.id === integration?.erp_system_id);
                  return (
                    <div
                      key={integration?.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <Database className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="font-medium text-gray-900">{system?.name}</p>
                          <p className="text-sm text-gray-500">
                            Last synced: {integration?.last_sync_at
                              ? new Date(integration.last_sync_at)?.toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            integration?.status === 'active' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {integration?.status}
                        </span>
                        <Button
                          onClick={() => handleEditIntegration(integration)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleTestConnection(integration?.id)}
                          variant="outline"
                          size="sm"
                          disabled={testingConnection === integration?.id}
                        >
                          {testingConnection === integration?.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDeleteIntegration(integration?.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {activeTab === 'schedules' && (
        <ExportSchedulePanel
          schedules={schedules}
          integrations={integrations}
          onRefresh={loadData}
        />
      )}
      {activeTab === 'history' && (
        <ExportHistoryTable
          history={exportHistory}
          onRefresh={loadData}
        />
      )}
      {activeTab === 'mapping' && (
        <FieldMappingEditor
          integrations={integrations}
          erpSystems={erpSystems}
        />
      )}
      {/* Modals */}
      {showConfigModal && (
        <IntegrationConfigModal
          integration={selectedIntegration}
          erpSystems={erpSystems}
          companyId={companyId}
          onClose={handleConfigModalClose}
        />
      )}
      {showExportWizard && (
        <ManualExportWizard
          integrations={integrations}
          companyId={companyId}
          onClose={handleExportWizardClose}
        />
      )}
    </div>
  );
};

export default DataExportIntegrationManager;